import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/multi-select";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronLeft } from "lucide-react";
import { Label } from "@/components/ui/label";
import { getCarServices } from "@/services/getCarServices";
import { useAuth } from "@/core/auth/contexts/AuthContext";
import { toast } from "sonner";
import { fetchEmployees } from "@/services/getEmployees";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/app/store";
import { useEffect, useMemo, useState } from "react";
import {
  CarService,
  CarServiceSubCategory,
} from "@/features/car-services/models/car-service.model";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  clearCreateOrderDetails,
  OrderDetailsState,
  setCreateOrderDetails,
} from "@/features/orders/createOrderDetailsSlice";
import { useCreateOrderMutation, useCreateExpressOrderMutation } from "@/features/orders/orderApi";
import withAuthorization from "@/core/auth/components/withAuthorization";
import { Role } from "@/core/auth/models/role.model";
import { getQueryParams } from "@/lib/getQueryParams";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

export interface AddOnsBySubCategory {
  subCategoryName: string;
  id: string;
  services: CarService[];
}

export interface ServicesObject {
  [key: string]: CarService;
}

export function POS() {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const plateNumber = getQueryParams(location).get("plateNumber");
  const createOrderDetails = useSelector(
    (state: RootState) => state.createOrderDetails
  );
  const [createOrder, { isLoading: isPosting }] = useCreateOrderMutation();
  const [expressServiceName, setExpressServiceName] = useState("");
  const [expressServiceCost, setExpressServiceCost] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState<Set<string>>(new Set());
  const [selectedExpressEmployee, setSelectedExpressEmployee] = useState("");
  const [selectedExpressEmployees, setSelectedExpressEmployees] = useState<Set<string>>(new Set());
  const [createExpressOrder] = useCreateExpressOrderMutation();
  // Handle employee selection for regular order
  const handleEmployeeChange = (value: string) => {
    setSelectedEmployee(value);
    let newOrderDetails: OrderDetailsState = createOrderDetails;
    newOrderDetails = {
      ...createOrderDetails,
      employee: value,
    };
    dispatch(
      setCreateOrderDetails({
        ...newOrderDetails,
      })
    );
  };

  // Handle multiple employee selection for regular order
  const handleEmployeesChange = (selectedValues: Set<string>) => {
    setSelectedEmployees(selectedValues);
    let newOrderDetails: OrderDetailsState = createOrderDetails;
    newOrderDetails = {
      ...createOrderDetails,
      employees: Array.from(selectedValues),
    };
    dispatch(
      setCreateOrderDetails({
        ...newOrderDetails,
      })
    );
  };

  // Handle employee selection for express order
  const handleExpressEmployeeChange = (value: string) => {
    setSelectedExpressEmployee(value);
  };

  // Handle multiple employee selection for express order
  const handleExpressEmployeesChange = (selectedValues: Set<string>) => {
    setSelectedExpressEmployees(selectedValues);
  };

  const handleAddRemovePackageItem = (itemId: string) => {
    let newOrderDetails: OrderDetailsState = createOrderDetails;
    newOrderDetails = {
      ...createOrderDetails,
      service: newOrderDetails.service === itemId ? "" : itemId,
      company: user.company._id,
    };
    dispatch(
      setCreateOrderDetails({
        ...newOrderDetails,
      })
    );
  };

  const handleNumberPlateChange = (event) => {
    let newOrderDetails: OrderDetailsState = createOrderDetails;
    newOrderDetails = {
      ...createOrderDetails,
      plateNumber: event.target.value,
    };
    dispatch(
      setCreateOrderDetails({
        ...newOrderDetails,
      })
    );
    const params = new URLSearchParams(location.search);
    params.set("plateNumber", event.target.value);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const handleAddRemoveAddonItem = (itemId: string) => {
    dispatch(
      setCreateOrderDetails({
        ...createOrderDetails,
        addOns: createOrderDetails.addOns.includes(itemId)
          ? createOrderDetails.addOns.filter((addOn) => addOn !== itemId)
          : [...createOrderDetails.addOns, itemId],
      })
    );
  };

  const handleAddRemoveAllAddonItems = (itemIds: (string | number)[]) => {
    const itemIdsStr = itemIds.map((item) => `${item}`);
    const allExist = itemIdsStr.every((id) =>
      createOrderDetails.addOns.includes(id)
    );

    dispatch(
      setCreateOrderDetails({
        ...createOrderDetails,
        addOns: allExist
          ? createOrderDetails.addOns.filter(
              (addOn) => !itemIdsStr.includes(addOn.toString())
            )
          : [...new Set([...createOrderDetails.addOns, ...itemIdsStr])],
      })
    );
  };

  const {
    data: carServices,
    isPending: carServicesLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["car-services"],
    queryFn: async () => getCarServices(),
    select: (data) => {
      const servicesMappedObject = {};
      const mappedData = data.map((service: CarService) => {
        servicesMappedObject[service?._id] = service;
        return {
          ...service,
          companyName: service.company?.name,
        };
      });
      return mappedData;
    },
    enabled: !!user?.company?._id || user?.role === "super-admin",
  });

  const { data: employees } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => {
      const data = await fetchEmployees();
      return data.map((employee: any) => {
        return { ...employee, company: employee.company?.name };
      });
    },
    enabled: !!user?.company?._id || user?.role === "super-admin",
  });

  const handleCreateOrder = async () => {
    // Filter addOns to only include valid add-on IDs
    const validAddOnIds = createOrderDetails.addOns.filter(
      (id) =>
        servicesObject[id] &&
        servicesObject[id].subCategory?.category?.name === "Addons"
    );
    const orderPayload = {
      ...createOrderDetails,
      addOns: validAddOnIds,
    };
    try {
      await createOrder(orderPayload).unwrap();
      dispatch(clearCreateOrderDetails());
      toast.success("Create order successful");
      navigate("/app/orders");
    } catch (error) {
      console.error("Failed to create order:", error);
      toast.error(error.data?.message ?? "An error occured");
    }
  };

  const handleCreateExpressOrder = async () => {
    try {
      await createExpressOrder({
        expressServiceName,
        expressServiceCost,
        plateNumber: createOrderDetails.plateNumber,
        company: user.company?._id,
        employee: selectedExpressEmployee,
        employees: Array.from(selectedExpressEmployees),
      }).unwrap();
      toast.success("Express order created successfully");
      navigate("/app/orders");
    } catch (error) {
      console.error("Failed to create express order:", error);
      toast.error(error.data?.message ?? "An error occurred");
    }
  };

  const primaryPackages = (services: CarService[]): CarService[] => {
    return services?.filter(
      (service) => service.subCategory?.category?.name === "Primary Packages"
    );
  };

  const addOnServices = (services: CarService[]): AddOnsBySubCategory[] => {
    const addOnSubCategories: Record<string, CarServiceSubCategory> = {};
    const addOnServices = services?.filter(
      (service) => service.subCategory?.category?.name === "Addons"
    );
    addOnServices.forEach((addOnService) => {
      addOnSubCategories[addOnService.subCategory?._id] =
        addOnService?.subCategory;
    });

    const addOnServicesbySubCategory: AddOnsBySubCategory[] = Object.keys(
      addOnSubCategories
    ).map((item) => {
      return {
        subCategoryName: addOnSubCategories[item]?.name,
        id: addOnSubCategories[item]?._id,
        services: addOnServices.filter(
          (service) => service.subCategory?._id === item
        ),
      };
    });

    return addOnServicesbySubCategory;
  };

  // Create employee options for multi-select
  const employeeOptions = employees?.map((emp) => ({
    label: emp.name || emp.fullName || emp.email,
    value: emp._id,
  })) || [];

  const servicesObject: ServicesObject = useMemo(() => {
    return (
      carServices?.reduce((acc, service) => {
        acc[service?._id] = service;
        return acc;
      }, {}) || {}
    );
  }, [carServices]);

  const totalCost = useMemo(() => {
    if (!carServices) return 0;
    // Sum add-on prices, skipping undefined
    const addonsTotal = createOrderDetails.addOns.reduce((acc: number, addon) => {
      const price = servicesObject[addon]?.price;
      return acc + (typeof price === "number" && !isNaN(price) ? price : 0);
    }, 0);
    // Main service price
    const mainServicePrice = (() => {
      const price = servicesObject[createOrderDetails.service]?.price;
      return typeof price === "number" && !isNaN(price) ? price : 0;
    })();
    return addonsTotal + mainServicePrice;
  }, [
    carServices,
    createOrderDetails.addOns,
    createOrderDetails.service,
    servicesObject,
  ]);

  useEffect(() => {
    let newOrderDetails: OrderDetailsState = createOrderDetails;
    if (plateNumber?.length) {
      newOrderDetails = {
        ...createOrderDetails,
        plateNumber: plateNumber,
      };
      dispatch(
        setCreateOrderDetails({
          ...newOrderDetails,
        })
      );
    } else {
      const params = new URLSearchParams(location.search);
      params.set("plateNumber", createOrderDetails.plateNumber);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [
    createOrderDetails,
    dispatch,
    location.pathname,
    location.search,
    navigate,
    plateNumber,
  ]);

  if (carServicesLoading) {
    return  [1, 2, 3, 4].map(() => (
              <div className="flex items-center space-x-4 w-full my-8">
                <div className="space-y-2 w-full">
                  <Skeleton className="h-4 w-[100%]" />
                  <Skeleton className="h-4 w-[100%]" />
                </div>
              </div>
            ));
  }

  return (
    <div className="h-full w-full md:p-8">
      <div className="md:p-4 p-2">
        {/* Modern Header */}
        <Card className="mb-8 shadow-sm border-0 bg-gradient-to-r from-primary/5 to-secondary/5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-6">
            <div className="flex items-center gap-4">
              <Link
                type="button"
                to="/app/orders"
                className="rounded-full bg-white/80 hover:bg-white border border-primary/20 shadow p-2 transition-colors text-primary dark:bg-primary/10 dark:hover:bg-primary/20"
                title="Back to Orders"
              >

                <ArrowLeft className="h-5 w-5 text-primary" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-primary mb-1">Create Order</h1>
                <p className="text-muted-foreground text-sm max-w-md">Easily create a new car service order, assign an employee, and add express or regular services for your customer.</p>
              </div>
            </div>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="default" className="font-semibold shadow-md">
                  + Create Express Order
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Express Order</DialogTitle>
                  <DialogDescription>
                    Enter express service details below
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="expressServiceName">Service Name</Label>
                    <Input
                      id="expressServiceName"
                      value={expressServiceName}
                      onChange={(e) => setExpressServiceName(e.target.value)}
                      placeholder="Enter service name"
                    />
                  </div>
                  {/* number plate  */}
                  <div className="grid gap-2">
                    <Label htmlFor="numberPlate">Car Number Plate</Label>
                    <Input
                      id="numberPlate"
                      type="text"
                      value={createOrderDetails.plateNumber}
                      onChange={handleNumberPlateChange}
                      placeholder="Enter car number plate"
                    />
                  </div>
                  {/* service cost  */}
                  <div className="grid gap-2">
                    <Label htmlFor="expressServiceCost">Service Cost</Label>
                    <Input
                      id="expressServiceCost"
                      type="number"
                      value={expressServiceCost}
                      onChange={(e) => setExpressServiceCost(e.target.value)}
                      placeholder="Enter cost"
                    />
                  </div>
                  {/* employee multi-select for express order */}
                  <div className="grid gap-2">
                    <Label htmlFor="expressEmployees">Assign Employees (Optional)</Label>
                    <MultiSelect
                      title="Select Employees"
                      options={employeeOptions}
                      selectedValues={selectedExpressEmployees}
                      onSelectionChange={handleExpressEmployeesChange}
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={handleCreateExpressOrder}
                    disabled={!expressServiceName || !expressServiceCost || !createOrderDetails.plateNumber}
                  >
                    Create Express Order
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </Card>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="mb-4 col-span-1 lg:col-span-2">
            <div>
              <div className="grid w-full max-w-sm items-center gap-1.5 my-4 mb-8">
                <Label className="text-lg mb-2" htmlFor="numberPlate">
                  Car Number Plate
                </Label>
                <Input
                  onChange={handleNumberPlateChange}
                  type="text"
                  placeholder="e.g KAB456H"
                  id="numberPlate"
                  value={createOrderDetails.plateNumber}
                />
                {/* employee multi-select for regular order */}
                <Label className="text-lg mb-2 mt-4" htmlFor="employees">
                  Assign Employees
                </Label>
                <MultiSelect
                  title="Select Employees"
                  options={employeeOptions}
                  selectedValues={selectedEmployees}
                  onSelectionChange={handleEmployeesChange}
                />
              </div>

              <h2 className="text-lg mb-6">Primary Packages</h2>
              <ScrollArea className="rounded-md border">
                <div className="flex gap-3 p-3 rounded-md">
                  {primaryPackages(carServices).map((servicePackage) => (
                    <Card
                      className="w-[250px] h-[200px] flex flex-col"
                      key={servicePackage?._id}
                    >
                      <CardHeader className="space-y-1 p-3 flex-1">
                        <CardTitle className="space-y-1 text-base flex justify-between">
                          <span>{servicePackage?.name}</span>
                          <span className="text-primary">
                            KSH {servicePackage?.price}
                          </span>
                        </CardTitle>
                        <CardDescription>
                          {servicePackage?.description}
                        </CardDescription>
                      </CardHeader>
                      <CardFooter className="flex justify-center p-3 align-bottom">
                        <Button
                          variant="outline"
                          onClick={() => {
                            handleAddRemovePackageItem(servicePackage?._id);
                          }}
                          className="w-full text-primary dark:bg-primary/10 dark:hover:bg-primary/20"
                        >
                          {createOrderDetails.service === servicePackage?._id
                            ? "Remove"
                            : "Select Package"}
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
                <ScrollBar orientation="horizontal" />
              </ScrollArea>
            </div>
            <div className="">
              <h2 className="text-lg mb-4 mt-6">Add Ons</h2>

              <div className="border gap-3 rounded-md p-3 flex flex-wrap">
                {addOnServices(carServices).map((subCategory) => (
                  <Card
                    className="md:min-w-[250px] w-full"
                    key={subCategory.id}
                  >
                    <CardHeader className="p-3">
                      <CardTitle className="text-base flex justify-between">
                        {subCategory.subCategoryName}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-3">
                      {subCategory.services?.map((service) => (
                        <div
                          key={service._id}
                          className="grid grid-cols-[25px_1fr] items-start pb-4 last:mb-0 last:pb-0"
                        >
                          <div className="">
                            <Checkbox
                              id={`${service._id}`}
                              checked={createOrderDetails.addOns.includes(
                                `${service._id}`
                              )}
                              onCheckedChange={() => {
                                handleAddRemoveAddonItem(`${service._id}`);
                              }}
                            />
                          </div>
                          <div className="space-y-1 flex flex-col">
                            <label
                              htmlFor={`${service._id}`}
                              className="text-sm font-medium leading-none flex justify-between"
                            >
                              <span>{service.name}</span>
                              <span className="text-primary">
                                KSH {service.price}
                              </span>
                            </label>
                            <label
                              htmlFor={`${service._id}`}
                              className="text-sm text-muted-foreground"
                            >
                              {service.description}
                            </label>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
          <div className="col-span-1">
            <div className="sticky top-4 border rounded-md p-3">
              <h2 className="text-lg mb-6">Order Summary</h2>
              <p className="text-sm text-muted-foreground">Car Number Plate</p>
              <p className="mb-2 text-sm">
                {createOrderDetails.plateNumber ? (
                  createOrderDetails.plateNumber
                ) : (
                  <span className="text-destructive">Not added</span>
                )}
              </p>
              <p className="text-sm text-muted-foreground">Service Package</p>
              <div className="mb-2 text-sm">
                <span>
                  {createOrderDetails.service ? (
                    <p className="flex justify-between">
                      <span>
                        {servicesObject[createOrderDetails.service]?.name}
                      </span>
                      <span className="text-primary">
                        {servicesObject[createOrderDetails.service]?.price}
                      </span>
                    </p>
                  ) : (
                    <span className="text-destructive text-sm">
                      Not selected
                    </span>
                  )}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">Add ons</p>
              {!createOrderDetails.addOns.length ? (
                <span className="text-sm text-warning">Not selected</span>
              ) : (
                createOrderDetails.addOns.map((addOn) => {
                  return (
                    <p
                      className="mb-1 text-sm flex justify-between"
                      key={servicesObject[addOn]?._id}
                    >
                      <span className="text-sm">
                        {servicesObject[addOn]?.name}
                      </span>
                      <span className="text-primary">
                        {servicesObject[addOn]?.price}
                      </span>
                    </p>
                  );
                })
              )}
              <div className="border rounded-md my-3 p-3 flex justify-between">
                <h2 className="text-lg text-muted-foreground">Total</h2>
                <p className="text-primary">
                  KSH {typeof totalCost === "number" && !isNaN(totalCost) ? totalCost.toLocaleString() : "0"}
                </p>
              </div>
              <div className="flex gap-1 mb-4 w-full mx-1">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      className="w-full"
                      disabled={
                        !createOrderDetails.plateNumber ||
                        !createOrderDetails.service
                      }
                    >
                      Create Order
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Confirm create order for car plate number{" "}
                        {createOrderDetails.plateNumber}
                      </AlertDialogTitle>
                      <AlertDialogDescription></AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleCreateOrder()}>
                        Create Order
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const POSPage = withAuthorization(POS, [Role.CompanyAdmin, Role.Employee]);

export default POSPage;